import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { FileText, ExternalLink } from "lucide-react";

const Section = ({ id, title, children, highlight = false }) => (
  <section id={id} className="py-8 border-b border-[#1E1E2E] last:border-0">
    <h2 className={`text-xl font-bold mb-4 ${highlight ? "text-[#71DED6]" : "text-[#F4F4F8]"}`}>
      {title}
    </h2>
    <div className="space-y-4 text-[#8B8BA7] leading-relaxed text-[15px]">
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1E1E2E] flex-shrink-0" />
    <span>{children}</span>
  </li>
);

const Strong = ({ children }) => (
  <span className="text-[#F4F4F8] font-medium">{children}</span>
);

export default function TermsOfServicePage() {
  usePageTitle("Terms of Service | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20">
      {/* Hero */}
      <div className="border-b border-[#1E1E2E] py-14">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-[#71DED6]" />
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">Legal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mb-3">
            Terms of Service
          </h1>
          <p className="text-[#8B8BA7] text-sm">Last updated: January 1, 2026</p>
        </div>
      </div>

      {/* Content */}
      <article className="py-12" data-testid="terms-of-service-content">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">

          <Section id="acceptance" title="Acceptance of Terms">
            <p>
              By accessing or using the CleanBid platform and services available at mycleanbid.ai (the "<Strong>Service</Strong>"), you agree to be bound by these Terms of Service ("<Strong>Terms</Strong>"). If you do not agree to these Terms, do not use the Service.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you ("<Strong>User</Strong>" or "<Strong>you</Strong>") and CleanBid ("<Strong>CleanBid</Strong>," "<Strong>we</Strong>," "<Strong>us</Strong>," or "<Strong>our</Strong>"). You represent that you have the legal authority to enter into this agreement on behalf of yourself or the business entity you represent.
            </p>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material changes by updating the "Last updated" date and, where appropriate, by sending notice to the email associated with your account. Continued use of the Service after changes take effect constitutes your acceptance of the revised Terms.
            </p>
          </Section>

          <Section id="description" title="Description of Service">
            <p>
              CleanBid is a revenue platform designed for home service businesses including, but not limited to, residential and commercial cleaning, pest control, lawn care, and exterior services. The Service enables users to:
            </p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet>Send professional quotes to customers via SMS.</Bullet>
              <Bullet>Automate follow-up sequences for open quotes and leads.</Bullet>
              <Bullet>Track jobs from lead through completion.</Bullet>
              <Bullet>Automatically send review requests upon job completion.</Bullet>
              <Bullet>Manage all customer communications through a unified inbox.</Bullet>
            </ul>
            <p>
              CleanBid is provided on a subscription basis. Features available to you depend on your selected plan. We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time with reasonable notice.
            </p>
          </Section>

          <Section id="account" title="Account Registration and Responsibilities">
            <p>
              To use the Service, you must register for an account by providing accurate, complete, and current information. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.
            </p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet>You must be at least 18 years old to create an account.</Bullet>
              <Bullet>You may not create an account on behalf of another person without their authorization.</Bullet>
              <Bullet>You agree to notify CleanBid immediately at <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">info@cleanbid.ai</a> of any unauthorized use of your account.</Bullet>
              <Bullet>You are responsible for all activity conducted under your account, including activity by any team members you grant access to.</Bullet>
              <Bullet>CleanBid is not liable for any loss or damage arising from your failure to secure your account credentials.</Bullet>
            </ul>
          </Section>

          {/* SMS Section — Highlighted */}
          <section id="sms-terms" className="py-8 border-b border-[#1E1E2E]">
            <h2 className="text-xl font-bold mb-4 text-[#71DED6]">
              SMS Messaging Terms
            </h2>
            <div className="bg-[#111118] border border-[#71DED6]/30 rounded-2xl p-6 space-y-4 text-[#8B8BA7] leading-relaxed text-[15px]">
              <p>
                CleanBid is an SMS-based platform. By using CleanBid's SMS features, you agree to the following terms in addition to all applicable federal and state laws:
              </p>
              <ul className="space-y-3 ml-2">
                <Bullet>
                  <Strong>Express Written Consent Required:</Strong> By using CleanBid, you agree to send SMS messages only to recipients who have provided express written consent to receive SMS communications from your business, as required by the Telephone Consumer Protection Act (TCPA) and applicable regulations. You represent and warrant that you have obtained such consent before using CleanBid to contact any individual.
                </Bullet>
                <Bullet>
                  <Strong>Legal Compliance:</Strong> You are solely responsible for ensuring your use of CleanBid's SMS features complies with all applicable laws and regulations, including but not limited to the <Strong>Telephone Consumer Protection Act (TCPA)</Strong>, the <Strong>CAN-SPAM Act</Strong>, the FCC's rules and regulations, and all applicable state and local laws governing electronic communications and telemarketing.
                </Bullet>
                <Bullet>
                  <Strong>Prohibited Uses:</Strong> You may not use CleanBid's SMS features to: (a) send spam, unsolicited messages, or bulk messages to individuals who have not provided express written consent; (b) send messages to numbers on the National Do Not Call Registry without proper exemption or consent; (c) use purchased, rented, or third-party contact lists where recipients have not specifically opted in to receive communications from your business; (d) engage in any form of deceptive or misleading messaging; or (e) violate any applicable carrier guidelines or CTIA messaging principles.
                </Bullet>
                <Bullet>
                  <Strong>Carrier Compliance:</Strong> CleanBid operates on carrier-approved messaging infrastructure (10DLC). You acknowledge that your use of the platform is subject to carrier policies and guidelines. CleanBid reserves the right to suspend or terminate accounts found to be in violation of carrier guidelines, TCPA requirements, or any applicable law, with or without prior notice.
                </Bullet>
                <Bullet>
                  <Strong>Delivery:</Strong> You acknowledge that SMS delivery is subject to carrier network availability, policies, and technical limitations and is not guaranteed. CleanBid is not liable for undelivered, delayed, or misdirected messages.
                </Bullet>
                <Bullet>
                  <Strong>Opt-Out Compliance:</Strong> You must honor all opt-out requests from recipients. When a recipient replies STOP, you agree not to send further messages to that number except as required by law. CleanBid's systems will automatically process STOP requests, but you remain responsible for compliance within your own business processes.
                </Bullet>
                <Bullet>
                  <Strong>Indemnification for SMS Violations:</Strong> You agree to indemnify and hold CleanBid harmless from any claims, damages, fines, penalties, or costs arising from your violation of these SMS Messaging Terms or applicable law.
                </Bullet>
              </ul>
            </div>
          </section>

          <Section id="billing" title="Subscription, Billing, and Cancellation">
            <p>
              Access to CleanBid is provided on a subscription basis. By subscribing, you authorize CleanBid to charge your payment method on a recurring basis at the rate associated with your selected plan.
            </p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet><Strong>Billing Cycle:</Strong> Subscriptions are billed monthly or annually, depending on the plan you select at signup.</Bullet>
              <Bullet><Strong>Annual Plans:</Strong> Annual subscriptions are billed upfront for the full year. Annual fees are non-refundable except as required by applicable law.</Bullet>
              <Bullet><Strong>Price Changes:</Strong> We reserve the right to change subscription pricing with at least 30 days' prior notice. Continued use of the Service after a price change constitutes your acceptance of the new pricing.</Bullet>
              <Bullet><Strong>Cancellation:</Strong> You may cancel your subscription at any time through your account settings. Your access will continue through the end of your current billing period. CleanBid does not provide partial refunds for unused time.</Bullet>
              <Bullet><Strong>Non-Payment:</Strong> If payment fails, we may suspend your account until payment is received. Continued non-payment may result in account termination.</Bullet>
              <Bullet><Strong>Taxes:</Strong> You are responsible for any applicable taxes associated with your subscription.</Bullet>
            </ul>
          </Section>

          <Section id="acceptable-use" title="Acceptable Use Policy">
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet>Use the Service in any way that violates applicable local, state, national, or international law or regulation.</Bullet>
              <Bullet>Transmit any material that is defamatory, obscene, fraudulent, or invasive of another's privacy.</Bullet>
              <Bullet>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</Bullet>
              <Bullet>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which may harm CleanBid or users of the Service.</Bullet>
              <Bullet>Attempt to gain unauthorized access to any portion of the Service or any other systems or networks connected to the Service.</Bullet>
              <Bullet>Use any robot, spider, scraper, or other automated means to access the Service for any purpose without express written permission.</Bullet>
              <Bullet>Reverse engineer, decompile, or otherwise attempt to derive the source code of any software component of the Service.</Bullet>
              <Bullet>Use the Service for competitive benchmarking or to build a competing product.</Bullet>
            </ul>
          </Section>

          <Section id="ip" title="Intellectual Property">
            <p>
              The Service and its original content, features, and functionality are and will remain the exclusive property of CleanBid. The CleanBid name, logo, and all related marks are trademarks of CleanBid. You may not use our trademarks without our prior written consent.
            </p>
            <p>
              You retain ownership of all data and content you upload to the Service. By using the Service, you grant CleanBid a limited, non-exclusive license to process, store, and use that data solely to provide the Service to you.
            </p>
          </Section>

          <Section id="liability" title="Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable law, in no event shall CleanBid, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet>Your access to or use of (or inability to access or use) the Service.</Bullet>
              <Bullet>Any conduct or content of any third party on the Service.</Bullet>
              <Bullet>Unauthorized access, use, or alteration of your transmissions or content.</Bullet>
              <Bullet>Undelivered, delayed, or misdirected SMS messages.</Bullet>
            </ul>
            <p>
              CleanBid's aggregate liability to you for any claims arising out of or relating to these Terms or the Service shall not exceed the total amount you paid to CleanBid in the three (3) months immediately preceding the event giving rise to the claim.
            </p>
            <p>
              Some jurisdictions do not allow the exclusion of certain warranties or the limitation of liability for certain damages. In such jurisdictions, CleanBid's liability is limited to the fullest extent permitted by law.
            </p>
          </Section>

          <Section id="indemnification" title="Indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless CleanBid and its officers, directors, employees, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:
            </p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet>Your violation of these Terms.</Bullet>
              <Bullet>Your use of the Service in violation of applicable law, including violations of the TCPA or other SMS-related regulations.</Bullet>
              <Bullet>Your infringement of any intellectual property or other rights of any person or entity.</Bullet>
              <Bullet>Any content or data you submit, post, or transmit through the Service.</Bullet>
            </ul>
          </Section>

          <Section id="termination" title="Termination">
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms or violate our Acceptable Use Policy or SMS Messaging Terms.
            </p>
            <p>
              Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or cancel through your account settings.
            </p>
            <p>
              Provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </Section>

          <Section id="governing-law" title="Governing Law">
            <p>
              These Terms shall be governed and construed in accordance with the laws of the <Strong>State of Connecticut, United States</Strong>, without regard to its conflict of law provisions.
            </p>
            <p>
              Any dispute arising from or relating to these Terms or the Service that cannot be resolved informally shall be subject to the exclusive jurisdiction of the state and federal courts located in the State of Connecticut. You consent to personal jurisdiction in such courts.
            </p>
          </Section>

          <Section id="changes" title="Changes to Terms">
            <p>
              We reserve the right to modify or replace these Terms at any time at our sole discretion. We will provide at least 30 days' notice of material changes by email or prominent in-app notification. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p>
              By continuing to access or use the Service after any revisions become effective, you agree to be bound by the revised Terms. If you do not agree to the new Terms, please stop using the Service.
            </p>
          </Section>

          <Section id="contact" title="Contact Us">
            <p>
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-[#111118] border border-[#1E1E2E] rounded-xl p-5 mt-3 space-y-1.5">
              <p><Strong>CleanBid.ai</Strong></p>
              <p>Email: <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">info@cleanbid.ai</a></p>
              <p>Website: <a href="https://mycleanbid.ai" className="text-[#71DED6] hover:underline">mycleanbid.ai</a></p>
            </div>
          </Section>

          {/* See also */}
          <div className="pt-8 mt-4 border-t border-[#1E1E2E] flex flex-col sm:flex-row items-start sm:items-center gap-3 text-sm text-[#8B8BA7]">
            <span>See also:</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy-policy" className="text-[#71DED6] hover:underline flex items-center gap-1">
                Privacy Policy <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>

        </div>
      </article>
    </div>
  );
}
